import React, { useState, useEffect } from 'react';
import {
  fetchBunningsItems,
  fetchBunningsSyncedItems,
  fetchBunningsCategories,
  fetchBunningsQuantityTypes,
  assignBunningsCategory,
  syncBunningsPrices,
  fetchBunningsConfig,
  updateBunningsConfig
} from '../../services/api';

import './BunningsDataPage.css';

const BunningsDataPage = ({ selectedRegion }) => { 

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Import state
  const [itemNumbers, setItemNumbers] = useState('');
  const [locationCode, setLocationCode] = useState('7040');
  const [importResults, setImportResults] = useState([]);
  
  // Synced items state
  const [syncedItems, setSyncedItems] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  
  // Categories and quantity types
  const [categories, setCategories] = useState([]);
  const [quantityTypes, setQuantityTypes] = useState([]);
  
  // Item assignment state
  const [itemAssignments, setItemAssignments] = useState({});
  
  // Configuration state
  const [config, setConfig] = useState({
    syncFrequency: 'quarterly',
    autoSync: false,
    locationCode: '7040'
  });
  
  // Dialog state
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Load initial data
  useEffect(() => {
    loadCategories();
    loadQuantityTypes();
    loadSyncedItems();
    loadConfig();
    loadSyncStatus();
  }, [selectedRegion]);

  const isAustraliaRegion = selectedRegion === 'vquest-au-1';

  // Block non-AU users immediately
  if (!isAustraliaRegion) {
    return (
      <div className="bunnings-access-denied">
        <div className="access-denied-card">
          <div className="access-denied-icon">🚫</div>
          <h2>Bunnings Integration Not Available</h2>
          <p>
            The Bunnings integration is only available for the <strong>Australia (vquest-au-1)</strong> region.
          </p>
          <p>
            You are currently viewing: <strong>{selectedRegion || 'Unknown'}</strong>
          </p>
          <div className="access-denied-actions">
            <p>To access Bunnings integration:</p>
            <ol>
              <li>Switch to the Australia region using the region selector in the header</li>
              <li>Navigate to the Bunnings Data page</li>
              <li>Import and manage Bunnings materials</li>
            </ol>
          </div>
          <div className="region-info">
            <strong>Why is this restricted?</strong>
            <p>
              Bunnings is an Australian hardware retailer. Materials imported via Bunnings
              are specific to the Australian market and pricing, so this integration is
              only available when working with the Australia database (vquest-au-1).
            </p>
          </div>
        </div>
      </div>
    );
  }

  // API call functions using imported functions
  const loadCategories = async () => {
    try {
      const data = await fetchBunningsCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadQuantityTypes = async () => {
    try {
      const data = await fetchBunningsQuantityTypes();
      setQuantityTypes(data.quantityTypes || []);
    } catch (err) {
      console.error('Failed to load quantity types:', err);
    }
  };

  const loadSyncedItems = async () => {
    try {
      const data = await fetchBunningsSyncedItems();
      setSyncedItems(data.items || []);
    } catch (err) {
      console.error('Failed to load synced items:', err);
    }
  };

  const loadConfig = async () => {
    try {
      const data = await fetchBunningsConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const loadSyncStatus = async () => {
    try {
      // Using direct fetch since we don't have a specific function for sync status
      const response = await fetch('/api/bunnings/sync-status');
      const data = await response.json();
      setSyncStatus(data);
    } catch (err) {
      console.error('Failed to load sync status:', err);
    }
  };

  // Import items handler
  const handleImportItems = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const items = itemNumbers
        .split(/[\n,]/)
        .map(s => s.trim())
        .filter(Boolean);
      
      if (items.length === 0) {
        throw new Error('Please enter at least one item number');
      }

      // Using imported function
      const result = await fetchBunningsItems(items);
      
      setImportResults(result.items || []);
      
      // Initialize assignments for new items
      const newAssignments = {};
      result.items.forEach(item => {
        if (item.success) {
          newAssignments[item.id] = {
            categoryId: '',
            quantityType: ''
          };
        }
      });
      setItemAssignments({ ...itemAssignments, ...newAssignments });
      
      setSuccess(`Successfully imported ${result.itemsImported} items. Please assign categories and quantity types below.`);
      setItemNumbers('');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Save item assignments
  const handleSaveAssignments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const assignmentsToSave = importResults
        .filter(item => item.success)
        .filter(item => {
          const assignment = itemAssignments[item.id];
          return assignment?.categoryId && assignment?.quantityType;
        });

      if (assignmentsToSave.length === 0) {
        throw new Error('Please assign categories and quantity types to at least one item');
      }

      // Call assign function for each item
      for (const item of assignmentsToSave) {
        const assignment = itemAssignments[item.id];
        await assignBunningsCategory(
          item.id,
          assignment.categoryId,
          parseInt(assignment.quantityType)
        );
      }

      setSuccess('Assignments saved successfully');
      setImportResults([]);
      setItemAssignments({});
      await loadSyncedItems();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Manual sync trigger
  const handleManualSync = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await syncBunningsPrices();
      setSuccess(result.message);
      
      await loadSyncedItems();
      await loadSyncStatus();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete item handler
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to remove this item from sync?')) {
      return;
    }

    try {
      // Using direct fetch since we don't have a specific delete function
      const response = await fetch(`/api/bunnings/items/${itemId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      setSuccess('Item removed from sync');
      await loadSyncedItems();
      
    } catch (err) {
      setError(err.message);
    }
  };

  // Save configuration
  const handleSaveConfig = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await updateBunningsConfig(config);
      setSuccess('Configuration saved successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // File upload handler
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      setItemNumbers(text);
    };
    reader.readAsText(file);
  };

  // Update assignment
  const updateAssignment = (itemId, field, value) => {
    setItemAssignments({
      ...itemAssignments,
      [itemId]: {
        ...itemAssignments[itemId],
        [field]: value
      }
    });
  };

  // Render Import Tab
  const renderImportTab = () => (
    <div className="tab-content">
      <h2>Import Bunnings Materials</h2>
      
      <div className="alert alert-info">
        <strong>ℹ️ Info:</strong> Enter Bunnings item numbers (7-digit codes) to import pricing data. 
        You can enter multiple items separated by commas or new lines.
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>❌ Error:</strong> {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <strong>✅ Success:</strong> {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <div className="import-layout">
        <div className="import-main">
          <div className="form-group">
            <label>Item Numbers</label>
            <textarea
              className="form-control"
              rows="10"
              placeholder="Enter item numbers (one per line or comma-separated)&#10;Example:&#10;0123456&#10;0234567&#10;0345678"
              value={itemNumbers}
              onChange={(e) => setItemNumbers(e.target.value)}
            />
            <small>Bunnings item numbers are 7-digit codes</small>
          </div>

          <div className="button-group">
            <button
              className="btn btn-primary"
              onClick={handleImportItems}
              disabled={loading || !itemNumbers.trim()}
            >
              {loading ? '⏳ Importing...' : '➕ Import Items'}
            </button>

            <label className="btn btn-secondary">
              📤 Upload File
              <input
                type="file"
                accept=".txt,.csv"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
            </label>
          </div>
        </div>

        <div className="import-sidebar">
          <div className="card">
            <h3>Settings</h3>
            
            <div className="form-group">
              <label>Location Code</label>
              <select
                className="form-control"
                value={locationCode}
                onChange={(e) => setLocationCode(e.target.value)}
              >
                <option value="7040">Sydney (7040)</option>
                <option value="7033">Melbourne (7033)</option>
                <option value="7041">Brisbane (7041)</option>
                <option value="7042">Perth (7042)</option>
                <option value="7043">Adelaide (7043)</option>
              </select>
            </div>

            <div className="info-text">
              <small>ℹ️ Prices will be fetched for the selected store location</small>
            </div>
          </div>

          <div className="card">
            <h3>Import Tips</h3>
            <ul className="tips-list">
              <li>Item numbers must be 7 digits</li>
              <li>Maximum 100 items per import</li>
              <li>Prices exclude GST</li>
              <li>Duplicates will be updated</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Import Results & Assignments */}
      {importResults.length > 0 && (
        <div className="import-results">
          <h3>Import Results - Assign Categories</h3>
          
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Item Number</th>
                <th>Name</th>
                <th>Price (excl GST)</th>
                <th>Category</th>
                <th>Quantity Type</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {importResults.map((item, index) => (
                <tr key={index}>
                  <td className="status-cell">
                    {item.success ? (
                      <span className="status-success">✅</span>
                    ) : (
                      <span className="status-error">❌</span>
                    )}
                  </td>
                  <td>{item.itemNumber}</td>
                  <td>{item.name || '-'}</td>
                  <td>{item.price ? `$${item.price.toFixed(2)}` : '-'}</td>
                  <td>
                    {item.success && (
                      <select
                        className="form-control-sm"
                        value={itemAssignments[item.id]?.categoryId || ''}
                        onChange={(e) => updateAssignment(item.id, 'categoryId', e.target.value)}
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.Id} value={cat.Id}>
                            {cat.Categories}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {item.success && (
                      <select
                        className="form-control-sm"
                        value={itemAssignments[item.id]?.quantityType || ''}
                        onChange={(e) => updateAssignment(item.id, 'quantityType', e.target.value)}
                      >
                        <option value="">Select Type</option>
                        {quantityTypes.map(qt => (
                          <option key={qt.Id} value={qt.Id}>
                            {qt.Name}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    <span className={item.success ? 'text-success' : 'text-error'}>
                      {item.message}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="button-group">
            <button
              className="btn btn-primary"
              onClick={handleSaveAssignments}
              disabled={loading}
            >
              {loading ? '⏳ Saving...' : '💾 Save Assignments'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Render Synced Items Tab
  const renderSyncedItemsTab = () => (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Synced Items ({syncedItems.length})</h2>
        
        <button
          className="btn btn-primary"
          onClick={handleManualSync}
          disabled={loading}
        >
          {loading ? '⏳ Syncing...' : '🔄 Sync Now'}
        </button>
      </div>

      {syncStatus && (
        <div className="alert alert-info">
          <strong>Last sync:</strong> {syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleString() : 'Never'}<br />
          <strong>Next scheduled sync:</strong> {syncStatus.nextSync ? new Date(syncStatus.nextSync).toLocaleString() : 'Not scheduled'}<br />
          <strong>Status:</strong> <span className="badge">{syncStatus.status || 'Unknown'}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <strong>❌ Error:</strong> {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <strong>✅ Success:</strong> {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Item Number</th>
            <th>Name</th>
            <th>Price (excl GST)</th>
            <th>Category</th>
            <th>Quantity Type</th>
            <th>Location</th>
            <th>Last Updated</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {syncedItems.length === 0 ? (
            <tr>
              <td colSpan="9" className="no-data">
                No items synced yet. Use the Import tab to add items.
              </td>
            </tr>
          ) : (
            syncedItems.map((item) => (
              <tr key={item.id}>
                <td>{item.itemNumber}</td>
                <td>{item.name}</td>
                <td>${item.price?.toFixed(2) || '-'}</td>
                <td>{item.category || '-'}</td>
                <td>{item.quantityType || '-'}</td>
                <td>{item.locationCode}</td>
                <td>
                  {item.lastUpdated ? new Date(item.lastUpdated).toLocaleDateString() : '-'}
                </td>
                <td>
                  <span className={`badge ${item.status === 'Active' ? 'badge-success' : 'badge-default'}`}>
                    {item.status || 'Active'}
                  </span>
                </td>
                <td>
                  <button
                    className="btn-icon"
                    onClick={() => { setSelectedItem(item); setShowItemDialog(true); }}
                    title="View Details"
                  >
                    ℹ️
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleDeleteItem(item.id)}
                    title="Remove"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  // Render Config Tab
  const renderConfigTab = () => (
    <div className="tab-content">
      <h2>Sync Configuration</h2>

      <div className="config-layout">
        <div className="config-section">
          <div className="card">
            <h3>Automatic Sync Settings</h3>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.autoSync}
                  onChange={(e) => setConfig({ ...config, autoSync: e.target.checked })}
                />
                Enable automatic sync
              </label>
            </div>

            <div className="form-group">
              <label>Sync Frequency</label>
              <select
                className="form-control"
                value={config.syncFrequency}
                onChange={(e) => setConfig({ ...config, syncFrequency: e.target.value })}
                disabled={!config.autoSync}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>

            <div className="form-group">
              <label>Default Location</label>
              <select
                className="form-control"
                value={config.locationCode}
                onChange={(e) => setConfig({ ...config, locationCode: e.target.value })}
              >
                <option value="7040">Sydney (7040)</option>
                <option value="7033">Melbourne (7033)</option>
                <option value="7041">Brisbane (7041)</option>
                <option value="7042">Perth (7042)</option>
                <option value="7043">Adelaide (7043)</option>
              </select>
            </div>
          </div>
        </div>

        <div className="config-section">
          <div className="card">
            <h3>API Status</h3>

            <div className="api-status">
              <div className="status-item">
                <span>Pricing Query API</span>
                <span className="badge badge-warning">Pending Approval</span>
              </div>

              <div className="status-item">
                <span>Item Query API</span>
                <span className="badge badge-warning">Pending Approval</span>
              </div>
            </div>

            <div className="alert alert-warning">
              <small>⚠️ APIs are currently pending approval. Mock data will be used for testing.</small>
            </div>
          </div>
        </div>
      </div>

      <div className="button-group">
        <button
          className="btn btn-primary"
          onClick={handleSaveConfig}
          disabled={loading}
        >
          {loading ? '⏳ Saving...' : '💾 Save Configuration'}
        </button>
      </div>

      {success && (
        <div className="alert alert-success">
          <strong>✅ Success:</strong> {success}
          <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <strong>❌ Error:</strong> {error}
          <button className="alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}
    </div>
  );

  // Render Item Details Dialog
  const renderItemDialog = () => {
    if (!showItemDialog || !selectedItem) return null;

    return (
      <div className="bunnings-modal-overlay" onClick={() => setShowItemDialog(false)}>
        <div className="bunnings-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="bunnings-modal-header">
            <h3>Item Details</h3>
            <button className="bunnings-modal-close" onClick={() => setShowItemDialog(false)}>×</button>
          </div>
          <div className="bunnings-modal-body">
            <div className="detail-grid">
              <div className="detail-item">
                <label>Item Number</label>
                <span>{selectedItem.itemNumber}</span>
              </div>
              <div className="detail-item">
                <label>Name</label>
                <span>{selectedItem.name}</span>
              </div>
              <div className="detail-item">
                <label>Price (excl GST)</label>
                <span>${selectedItem.price?.toFixed(2)}</span>
              </div>
              <div className="detail-item">
                <label>Category</label>
                <span>{selectedItem.category || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Quantity Type</label>
                <span>{selectedItem.quantityType || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Location</label>
                <span>{selectedItem.locationCode}</span>
              </div>
              <div className="detail-item">
                <label>SKU</label>
                <span>{selectedItem.sku || '-'}</span>
              </div>
              <div className="detail-item">
                <label>Last Updated</label>
                <span>
                  {selectedItem.lastUpdated ? new Date(selectedItem.lastUpdated).toLocaleString() : '-'}
                </span>
              </div>
            </div>
          </div>
          <div className="bunnings-modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowItemDialog(false)}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bunnings-page">
      <div className="page-header">
        <h1>Bunnings Materials Integration</h1>
        <p>Import and manage Bunnings product pricing for Australian region materials.</p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 0 ? 'active' : ''}`}
          onClick={() => setActiveTab(0)}
        >
          Import Items
        </button>
        <button
          className={`tab ${activeTab === 1 ? 'active' : ''}`}
          onClick={() => setActiveTab(1)}
        >
          Synced Items ({syncedItems.length})
        </button>
        <button
          className={`tab ${activeTab === 2 ? 'active' : ''}`}
          onClick={() => setActiveTab(2)}
        >
          Configuration
        </button>
      </div>

      {activeTab === 0 && renderImportTab()}
      {activeTab === 1 && renderSyncedItemsTab()}
      {activeTab === 2 && renderConfigTab()}

      {renderItemDialog()}
    </div>
  );
};

export default BunningsDataPage;